import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  CreatePackageCategoryFormType,
  CreatePackageCategoryResponse,
  CreatePackageSubCategoryFormType,
  CreatePackageSubCategoryResponse,
  EditPackageCategoryFormType,
  EditPackageCategoryResponse,
  EditPackageSubCategoryFormType,
  EditPackageSubCategoryResponse,
  PackageCategory,
  PackageCategoryFormType,
  PackageSubCategory,
  PackageSubCategoryFormType,
} from "../api/interfaces";
import {
  useCreatePackageCategory,
  useCreatePackageSubCategory,
  useEditPackageCategory,
  useEditPackageSubCategory,
} from "../api/mutations";
import { useGetPackageCategory } from "../api/queries";
import {
  packageCategoryFormSchema,
  packageSubCategoryFormSchema,
} from "../packageCategorySchema";
import { apiErrorHandler } from "src/utils/apiErrorHandler";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";

interface HandleSubmitCreateProps {
  form: UseFormReturn<CreatePackageCategoryFormType, any, any>;
  mutation: UseMutationResult<
    CreatePackageCategoryResponse,
    Error,
    CreatePackageCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitCreatePackageSubCategoryProps {
  packageCategoryId: number;
  form: UseFormReturn<PackageSubCategoryFormType, any, any>;
  mutation: UseMutationResult<
    CreatePackageSubCategoryResponse,
    Error,
    CreatePackageSubCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitEditProps {
  id: string;
  form: UseFormReturn<PackageCategoryFormType, any, undefined>;
  mutation: UseMutationResult<
    EditPackageCategoryResponse,
    Error,
    EditPackageCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitEditPackageSubCategoryProps {
  id: string;
  packageCategoryId: number;
  form: UseFormReturn<PackageSubCategoryFormType, any, undefined>;
  mutation: UseMutationResult<
    EditPackageSubCategoryResponse,
    Error,
    EditPackageSubCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

export const handleSubmitCreatePackageCategory = async ({
  form,
  mutation,
  handleClose,
}: HandleSubmitCreateProps) => {
  const values = form.getValues();

  const payload = {
    name: values.name,
    description: values.description,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

export const handleSubmitCreatePackageSubCategory = async ({
  packageCategoryId,
  form,
  mutation,
  handleClose,
}: HandleSubmitCreatePackageSubCategoryProps) => {
  const values = form.getValues();

  const payload = {
    packageCategoryId,
    name: values.name,
    color: values.color,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const handleSubmitEdit = async ({
  id,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditProps) => {
  const values = form.getValues();

  const payload = {
    id,
    name: values.name,
    description: values.description,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const handleSubmitEditPackageSubCategory = async ({
  id,
  packageCategoryId,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditPackageSubCategoryProps) => {
  const values = form.getValues();

  const payload = {
    id,
    packageCategoryId,
    name: values.name,
    color: values.color,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const usePackageCategoryHandler = () => {
  const isAllowToCreate = useGetAccessPermission({
    resource: AccessResource["Master Data - Package Category"],
    action: ActionType["Create"],
  });

  const [isShowFormModal, setIsShowFormModal] = useState(false);
  const [
    isShowPackageSubCategoryFormModal,
    setIsShowPackageSubCategoryFormModal,
  ] = useState(false);

  const [selectedData, setSelectedData] = useState<
    PackageCategory | PackageSubCategory | null
  >(null);
  const [selectedDataParentId, setSelectedDataParentId] = useState<
    number | null
  >(null);

  const { data, isLoading } = useGetPackageCategory();

  const form = useForm<z.infer<typeof packageCategoryFormSchema>>({
    resolver: zodResolver(packageCategoryFormSchema),
  });
  const packageSubCategoryForm = useForm<
    z.infer<typeof packageSubCategoryFormSchema>
  >({
    resolver: zodResolver(packageSubCategoryFormSchema),
  });

  const createMutation = useCreatePackageCategory();
  const editMutation = useEditPackageCategory();

  const createPackageSubCategoryMutation = useCreatePackageSubCategory();
  const editPackageSubCategoryMutation = useEditPackageSubCategory();

  const handleAdd = () => setIsShowFormModal(true);
  const handleCloseAdd = () => {
    setIsShowFormModal(false);
    form.reset({ name: "", description: "" });
  };

  const handleEdit = (data: PackageCategory) => {
    setIsShowFormModal(true);
    setSelectedData(data);
    form.reset({
      name: data?.name,
      description: data.description ?? "",
    });
  };
  const handleCloseEdit = () => {
    setIsShowFormModal(false);
    setSelectedData(null);
    form.reset({ name: "", description: "" });
  };

  const handleAddPackageSubCategory = (parentId: number) => {
    setSelectedDataParentId(parentId);
    setIsShowPackageSubCategoryFormModal(true);
  };
  const handleCloseAddPackageSubCategory = () => {
    setSelectedDataParentId(null);
    setIsShowPackageSubCategoryFormModal(false);
    packageSubCategoryForm.reset({ name: "", color: "" });
  };

  const handleEditPackageSubCategory = (data: PackageSubCategory) => {
    setIsShowPackageSubCategoryFormModal(true);
    setSelectedData(data);
    setSelectedDataParentId(data.package_category_id);
    packageSubCategoryForm.reset({
      name: data?.name,
      color: data.hex,
    });
  };
  const handleCloseEditPackageSubCategory = () => {
    setIsShowPackageSubCategoryFormModal(false);
    setSelectedData(null);
    setSelectedDataParentId(null);
    packageSubCategoryForm.reset({ name: "", color: "" });
  };

  return {
    isLoading,
    isAllowToCreate,
    packageCategory: data?.data,
    isShowFormModal,
    isShowPackageSubCategoryFormModal,
    handleAdd,
    handleEdit,
    handleAddPackageSubCategory,
    handleEditPackageSubCategory,
    form,
    packageSubCategoryForm,
    formType: (!selectedData ? "CREATE" : "EDIT") as "CREATE" | "EDIT",
    handleCancelFormModal: () =>
      !selectedData ? handleCloseAdd() : handleCloseEdit(),
    handleSubmitFormModal: form.handleSubmit(
      async () =>
        await (!selectedData
          ? handleSubmitCreatePackageCategory({
              form,
              mutation: createMutation,
              handleClose: handleCloseAdd,
            })
          : handleSubmitEdit({
              id: `${selectedData?.id}`,
              form,
              mutation: editMutation,
              handleClose: handleCloseEdit,
            }))
    ),
    handleCancelPackageSubCategoryFormModal: () =>
      !selectedData
        ? handleCloseAddPackageSubCategory()
        : handleCloseEditPackageSubCategory(),
    handleSubmitPackageSubCategoryFormModal:
      packageSubCategoryForm.handleSubmit(
        async () =>
          await (!selectedData
            ? handleSubmitCreatePackageSubCategory({
                packageCategoryId: selectedDataParentId as number,
                form: packageSubCategoryForm,
                mutation: createPackageSubCategoryMutation,
                handleClose: handleCloseAddPackageSubCategory,
              })
            : handleSubmitEditPackageSubCategory({
                id: `${selectedData?.id}`,
                packageCategoryId: selectedDataParentId as number,
                form: packageSubCategoryForm,
                mutation: editPackageSubCategoryMutation,
                handleClose: handleCloseEditPackageSubCategory,
              }))
      ),
  };
};

export default usePackageCategoryHandler;
